import * as React from 'react';
import { useSelector, useDispatch, useStore, shallowEqual } from 'react-redux';
import { chunk, flatten } from 'lodash';
import { selectIsAppsDemoModeEnabled } from '@/selectors/selectors';
import type { RootState } from '@/reducers/reducers';
import { useLazyGetStepRunsSummaryQuery } from '@/middlewares/defaultApi';
import type { StepRun } from '@/types/stepRun';
import type { AutomationRun } from '@/types';
import { stepRunsSummaryActions } from '@/reducers/stepRunsSummary';
import {
    selectStepRunsFromSummary,
    selectLoadedStepRunIds,
    selectStepGroupAutomationRuns,
} from '@/selectors/selectors';

const DEFAULT_PAGE_SIZE = 10;

export interface UseStepRunsSummaryInput {
    orgId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunIds: string[];
    pageSize?: number;
    skip?: boolean;
}

export interface UseStepRunsSummaryResult {
    stepRuns: StepRun[];
    stepGroupAutomationRuns: AutomationRun[];
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    hasMore: boolean;
    fetchMore: () => void;
    fetchAll: (filteredStepRunIds?: string[]) => Promise<StepRun[]>;
}

export const useStepRunsSummary = ({
    orgId,
    workflowId,
    workflowRunId,
    stepRunIds,
    pageSize = DEFAULT_PAGE_SIZE,
    skip = false,
}: UseStepRunsSummaryInput): UseStepRunsSummaryResult => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoStepRunsById = useSelector(
        (state: RootState) => state.ui.uiConfig.demoData?.stepRunsDemoData ?? {},
        shallowEqual,
    );

    const stepRunIdsKey = stepRunIds.join(',');
    const chunks = React.useMemo(() => chunk(stepRunIds, pageSize), [stepRunIdsKey, pageSize]);

    const chunksRef = React.useRef(chunks);
    chunksRef.current = chunks;

    const cacheKey = `${orgId}-${workflowRunId}-${stepRunIdsKey}`;

    const [pageIndex, setPageIndex] = React.useState(0);

    const [isLoading, setIsLoading] = React.useState(() => {
        if (!chunks[0] || skip || !stepRunIdsKey) return false;
        // Check if first chunk is already in the Redux slice
        const loadedIds = selectLoadedStepRunIds(store.getState(), workflowRunId);
        const allLoaded = chunks[0].every((id) => loadedIds.has(id));
        return !allLoaded;
    });
    const [isFetching, setIsFetching] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    React.useEffect(() => {
        setPageIndex(0);
        setError(null);
        setIsLoading(false);
    }, [cacheKey]);

    const [fetchBatch] = useLazyGetStepRunsSummaryQuery();

    // Read all loaded step runs for the requested id set. This allows targeted fetchAll()
    // calls to immediately hydrate the visible list, even when they fetch ids beyond pageIndex.
    const stepRuns = useSelector((state: RootState) => {
        if (isViewingAppsInDemoMode) {
            return stepRunIds.map((id) => demoStepRunsById[id]).filter((sr): sr is StepRun => !!sr);
        }

        const loadedIdsSet = selectLoadedStepRunIds(state, workflowRunId);
        const loadedRequestedIds = stepRunIds.filter((id) => loadedIdsSet.has(id));
        return selectStepRunsFromSummary(state, workflowRunId, loadedRequestedIds);
    }, shallowEqual);

    const archetypeOfGroup = stepRuns[0]?.type;
    const stepGroupAutomationRuns = useSelector(
        (state: RootState) => selectStepGroupAutomationRuns(state, workflowRunId, archetypeOfGroup),
        shallowEqual,
    );

    const fetchPage = React.useCallback(
        async (index: number) => {
            const currentChunks = chunksRef.current;
            if (index >= currentChunks.length) return;

            const chunkIds = currentChunks[index];

            // Check if all IDs in this chunk are already in the slice
            const loadedIds = selectLoadedStepRunIds(store.getState(), workflowRunId);
            const allLoaded = chunkIds.every((id) => loadedIds.has(id));
            if (allLoaded) {
                setPageIndex(index + 1);
                return;
            }

            index === 0 ? setIsLoading(true) : setIsFetching(true);

            const result = await fetchBatch(
                {
                    orgId,
                    workflowId,
                    workflowRunId,
                    stepRunIds: chunkIds,
                },
                /* preferCacheValue */ true,
            );

            index === 0 ? setIsLoading(false) : setIsFetching(false);

            if (result.error) {
                setError(result.error);
                return;
            }

            setError(null);

            if (result.data?.stepRuns) {
                dispatch(
                    stepRunsSummaryActions.upsertStepRuns({
                        workflowRunId,
                        stepRuns: result.data.stepRuns,
                    }),
                );
            }
            const stepGroupAutomationRuns = result.data?.stepGroupAutomationRuns;
            const archetype = result.data?.stepRuns?.[0]?.type;
            if (stepGroupAutomationRuns?.length && archetype) {
                dispatch(
                    stepRunsSummaryActions.upsertStepGroupAutomationRuns({
                        workflowRunId,
                        archetype,
                        automationRuns: stepGroupAutomationRuns,
                    }),
                );
            }

            setPageIndex(index + 1);
        },
        [orgId, workflowId, workflowRunId, fetchBatch, dispatch, store],
    );

    React.useEffect(() => {
        if (skip || isViewingAppsInDemoMode || !stepRunIdsKey) return;
        fetchPage(0);
    }, [cacheKey, skip, isViewingAppsInDemoMode, fetchPage]);

    const fetchMore = React.useCallback(() => {
        fetchPage(pageIndex);
    }, [fetchPage, pageIndex]);

    // Fetches multiple pages for either:
    // 1) all remaining chunks from current pageIndex (default), or
    // 2) a targeted set of stepRunIds (used for status-scoped selection).
    const fetchAll = React.useCallback(
        async (filteredStepRunIds?: string[]): Promise<StepRun[]> => {
            if (isViewingAppsInDemoMode) {
                const ids = filteredStepRunIds?.length ? filteredStepRunIds : stepRunIds;
                return ids.map((id) => demoStepRunsById[id]).filter((sr): sr is StepRun => !!sr);
            }

            // Shared helper: fetches each chunk, dispatches results, and accumulates into accumulatedById.
            // Calls onChunkDone(chunkIds) after each chunk completes (hit or miss), for callers that need
            // to advance pageIndex. Returns false if a fetch error occurred.
            const fetchChunks = async (
                chunksToFetch: string[][],
                accumulatedById: Map<string, StepRun>,
                onChunkDone?: (chunkIds: string[]) => void,
            ): Promise<boolean> => {
                for (const chunkIds of chunksToFetch) {
                    const loadedIds = selectLoadedStepRunIds(store.getState(), workflowRunId);
                    const allLoaded = chunkIds.every((id) => loadedIds.has(id));
                    if (allLoaded) {
                        selectStepRunsFromSummary(store.getState(), workflowRunId, chunkIds).forEach((sr) =>
                            accumulatedById.set(sr.id, sr),
                        );
                        onChunkDone?.(chunkIds);
                        continue;
                    }

                    const result = await fetchBatch({ orgId, workflowId, workflowRunId, stepRunIds: chunkIds }, true);
                    if (result.error) {
                        setError(result.error);
                        return false;
                    }
                    if (result.data?.stepRuns) {
                        result.data.stepRuns.forEach((sr) => accumulatedById.set(sr.id, sr));
                        dispatch(
                            stepRunsSummaryActions.upsertStepRuns({ workflowRunId, stepRuns: result.data.stepRuns }),
                        );
                    }
                    const stepGroupAutomationRuns = result.data?.stepGroupAutomationRuns;
                    const archetype = result.data?.stepRuns?.[0]?.type;
                    if (stepGroupAutomationRuns?.length && archetype) {
                        dispatch(
                            stepRunsSummaryActions.upsertStepGroupAutomationRuns({
                                workflowRunId,
                                archetype,
                                automationRuns: stepGroupAutomationRuns,
                            }),
                        );
                    }
                    onChunkDone?.(chunkIds);
                }
                return true;
            };

            if (filteredStepRunIds && filteredStepRunIds.length > 0) {
                const uniqueIds = Array.from(new Set(filteredStepRunIds));
                const accumulatedById = new Map<string, StepRun>();
                selectStepRunsFromSummary(store.getState(), workflowRunId, uniqueIds).forEach((sr) =>
                    accumulatedById.set(sr.id, sr),
                );
                setIsFetching(true);
                try {
                    const ok = await fetchChunks(chunk(uniqueIds, pageSize), accumulatedById);
                    if (ok) setError(null);
                    return Array.from(accumulatedById.values());
                } finally {
                    setIsFetching(false);
                }
            }

            const currentChunks = chunksRef.current;
            const startIndex = pageIndex;
            const chunkBatchSize = 2;
            const accumulatedById = new Map<string, StepRun>();
            for (const sr of stepRuns) {
                accumulatedById.set(sr.id, sr);
            }

            if (startIndex >= currentChunks.length) {
                return Array.from(accumulatedById.values());
            }

            // Flatten groups of chunkBatchSize existing chunks into larger batches for fewer requests.
            const batches: string[][] = [];
            for (let i = startIndex; i < currentChunks.length; i += chunkBatchSize) {
                batches.push(flatten(currentChunks.slice(i, i + chunkBatchSize)));
            }

            let batchStartIndex = startIndex;
            setIsFetching(true);
            try {
                const ok = await fetchChunks(batches, accumulatedById, () => {
                    const nextIndex = Math.min(batchStartIndex + chunkBatchSize, currentChunks.length);
                    setPageIndex(nextIndex);
                    batchStartIndex = nextIndex;
                });
                if (ok) setError(null);
                return Array.from(accumulatedById.values());
            } finally {
                setIsFetching(false);
            }
        },
        [
            isViewingAppsInDemoMode,
            demoStepRunsById,
            stepRunIds,
            orgId,
            workflowId,
            workflowRunId,
            fetchBatch,
            pageIndex,
            stepRuns,
            dispatch,
            store,
            pageSize,
        ],
    );

    return {
        stepRuns,
        stepGroupAutomationRuns,
        isLoading,
        isFetching,
        error,
        hasMore: !isViewingAppsInDemoMode && pageIndex < chunks.length,
        fetchMore,
        fetchAll,
    };
};
